import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, Check, Zap, Sparkles, Users, MessageSquare, BarChart3, Shield, LayoutGrid, Clock, Star } from 'lucide-react';
import SEO from '@/components/SEO';
import Icon from '@/components/Icon';
import BrandIcon from '@/components/BrandIcon';
import BrandMarquee from '@/components/BrandMarquee';
import PaymentMethods from '@/components/PaymentMethods';
import { site, products, channels, pricingPlans } from '@/data/content';
import { useLocation } from '@/contexts/LocationContext';

export default function Product() {
  const { convertPrice, formatPrice } = useLocation();
  const productFaqs = [
    {
      q: 'What is ZuriHub?',
      a: 'ZuriHub is an AI-first customer experience platform that unifies live chat, tickets, knowledge base, messaging, AI voice, and analytics in one workspace.',
    },
    {
      q: 'Do I need separate tools for chat, tickets, and AI?',
      a: 'No. ZuriHub replaces chat widgets, help desks, and AI add-ons with one platform, so your team works from a single inbox.',
    },
    {
      q: 'Which channels does ZuriHub support?',
      a: 'WhatsApp, Instagram, Telegram, Messenger, X, Gmail, live chat, and email all route into the same shared inbox.',
    },
    {
      q: 'Is ZuriHub built for small businesses?',
      a: 'Yes. Start free for one agent, then upgrade to Pro at $25 per agent per month as your team grows.',
    },
  ];

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'ZuriHub Product Overview',
      url: `${site.baseUrl}/product`,
      description: `${site.name} unifies AI, cloud, and voice into one customer experience workspace.`,
    },
    {
      '@context': 'https://schema.org',
      '@type': 'ProductGroup',
      name: 'ZuriHub Products',
      description: site.description,
      brand: { '@type': 'Brand', name: site.name },
      hasVariant: products.map((p) => ({
        '@type': 'Product',
        name: p.name,
        description: p.description,
      })),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: productFaqs.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    },
  ];

  const productIcons: Record<string, string> = {
    'zyra-ai': 'Sparkles',
    'zuri-cloud': 'Cloud',
    'zuri-voice': 'Phone',
  };

  return (
    <>
      <SEO
        title="Product"
        description="Meet Zyra AI, Zuri Cloud, and Zuri Voice. The products that power ZuriHub."
        path="/product"
        jsonLd={jsonLd}
      >
        <meta
          name="keywords"
          content="ZuriHub product, AI customer service platform, Zyra AI, Zuri Cloud, Zuri Voice, omnichannel inbox, customer experience software"
        />
      </SEO>

      <section className="relative overflow-hidden bg-slate-950 px-4 pt-32 pb-32 text-white sm:px-6 lg:px-8">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-brand-900/40 via-slate-950 to-slate-950" />
        <div className="relative mx-auto max-w-6xl">
          <div className="grid gap-12 lg:grid-cols-2 lg:gap-16">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full border border-brand-500/30 bg-brand-500/10 px-4 py-1.5 text-sm font-semibold text-brand-300 backdrop-blur">
                <Zap className="h-4 w-4" /> One platform. Three products.
              </span>
              <h1 className="mt-6 text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
                The complete <span className="gradient-text">CX stack</span> for modern teams
              </h1>
              <p className="mt-6 text-lg text-slate-300">
                ZuriHub unifies AI, cloud, and voice into one workspace. Serve customers across every channel without switching tools.
              </p>
              <div className="mt-8 flex flex-col gap-4 sm:flex-row">
                <a
                  href={site.workspaceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-8 py-4 text-base font-bold text-slate-900 shadow-xl shadow-white/10 transition hover:bg-slate-100"
                >
                  Start Free Trial <ArrowUpRight className="h-4 w-4" />
                </a>
                <Link
                  to="/features"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-600 bg-slate-900/50 px-8 py-4 text-base font-semibold text-white backdrop-blur transition hover:border-brand-500 hover:bg-slate-800"
                >
                  Explore Features <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
              <div className="mt-8 flex items-center gap-6 text-sm text-slate-400">
                <div className="flex items-center gap-2">
                  <Icon name="Star" className="h-4 w-4 text-yellow-400" />
                  <span>4.8/5 from 250+ teams</span>
                </div>
                <div className="flex items-center gap-2">
                  <Shield className="h-4 w-4 text-green-400" />
                  <span>Enterprise security</span>
                </div>
              </div>
            </div>
            <div className="relative hidden lg:block">
              <div className="absolute inset-0 bg-gradient-to-br from-brand-600/20 to-brand-400/10 blur-3xl" />
              <div className="relative rounded-3xl border border-slate-700 bg-slate-900/60 p-2 backdrop-blur">
                <img
                  src="https://media.getzurihub.com/Jayrah%20Assets/SI-Map-Photo-Channels.webp"
                  alt="ZuriHub channels map"
                  className="rounded-2xl w-full h-auto"
                />
                <div className="absolute bottom-4 left-4 right-4 flex flex-wrap gap-2">
                  {[
                    { label: 'Zyra AI', color: 'bg-purple-500' },
                    { label: 'Zuri Cloud', color: 'bg-blue-500' },
                    { label: 'Zuri Voice', color: 'bg-green-500' },
                  ].map((item) => (
                    <div
                      key={item.label}
                      className="flex items-center gap-2 rounded-full border border-slate-800 bg-slate-900/80 px-4 py-2 text-sm font-semibold text-white backdrop-blur"
                    >
                      <div className={`h-2 w-2 rounded-full ${item.color}`} />
                      {item.label}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <BrandMarquee compact />

      <section className="py-24" aria-labelledby="products-list">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <span className="inline-flex items-center gap-2 rounded-full border border-brand-200 bg-brand-50 px-4 py-1.5 text-sm font-semibold text-brand-700">
              <LayoutGrid className="h-4 w-4" /> Integrated platform
            </span>
            <h2 id="products-list" className="mt-6 text-3xl font-extrabold text-slate-900 sm:text-4xl">Three products. One workspace.</h2>
            <p className="mt-4 text-lg text-slate-600">No switching tools. No fragmented data. Just seamless customer experiences.</p>
          </div>
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {products.map((product, index) => (
              <div
                key={product.id}
                className={`group relative overflow-hidden rounded-3xl border bg-white p-8 shadow-sm transition-all hover:-translate-y-1 hover:shadow-xl ${
                  index === 1 ? 'border-brand-200 shadow-lg shadow-brand-100/50 md:scale-[1.02]' : 'border-slate-100'
                }`}
              >
                {index === 1 && (
                  <span className="absolute right-6 top-6 rounded-full bg-brand-600 px-3 py-1 text-xs font-bold text-white shadow-lg shadow-brand-500/30">
                    Core Platform
                  </span>
                )}
                <div className={`mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br shadow-inner transition ${
                  index === 1 
                    ? 'from-brand-600 to-brand-400 text-white' 
                    : 'from-brand-100 to-brand-50 text-brand-700 group-hover:from-brand-600 group-hover:to-brand-700 group-hover:text-white'
                }`}>
                  <Icon name={productIcons[product.id]} className="h-8 w-8" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900">{product.name}</h3>
                <p className={`mt-2 font-semibold ${index === 1 ? 'text-brand-600' : 'text-slate-600'}`}>{product.tagline}</p>
                <p className="mt-4 text-slate-600">{product.description}</p>
                <ul className="mt-6 space-y-3">
                  {product.benefits.map((b) => (
                    <li key={b} className="flex items-start gap-2 text-sm font-semibold text-slate-700">
                      <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-green-600" />
                      {b}
                    </li>
                  ))}
                </ul>
                <a
                  href={site.workspaceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold transition ${
                    index === 1 
                      ? 'bg-brand-600 text-white hover:bg-brand-700' 
                      : 'bg-slate-900 text-white hover:bg-slate-800'
                  }`}
                >
                  Try {product.name} <ArrowUpRight className="h-4 w-4" />
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-gradient-to-br from-slate-50 to-white py-24" aria-labelledby="how-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <span className="inline-flex items-center gap-2 rounded-full border border-brand-200 bg-brand-50 px-4 py-1.5 text-sm font-semibold text-brand-700">
              <Clock className="h-4 w-4" /> Quick setup
            </span>
            <h2 id="how-heading" className="mt-6 text-3xl font-extrabold text-slate-900 sm:text-4xl">Set up in minutes. Scale forever.</h2>
            <p className="mt-4 text-lg text-slate-600">Three simple steps to transform your customer experience.</p>
          </div>
          <div className="mt-14 grid gap-8 md:grid-cols-3">
            {[
              {
                step: '01',
                icon: 'Globe',
                title: 'Connect your channels',
                description:
                  'Link WhatsApp, email, chat, and social so every conversation lands in one inbox.',
                color: 'from-blue-500 to-blue-600',
              },
              {
                step: '02',
                icon: 'Sparkles',
                title: 'Train Zyra AI',
                description:
                  'Point Zyra at your docs and past tickets. It learns your tone, policies, and answers.',
                color: 'from-purple-500 to-purple-600',
              },
              {
                step: '03',
                icon: 'Users',
                title: 'Serve customers',
                description:
                  'Agents use smart replies, summaries, and omnichannel context to close tickets faster.',
                color: 'from-green-500 to-green-600',
              },
            ].map((s, index) => (
              <div
                key={s.step}
                className="group relative overflow-hidden rounded-3xl border border-slate-200 bg-white p-8 shadow-sm transition-all hover:-translate-y-1 hover:shadow-xl"
              >
                <div className="absolute right-0 top-0 h-24 w-24 translate-x-8 -translate-y-8 rounded-full bg-gradient-to-br from-slate-100 to-slate-50 opacity-50" />
                <div className="relative mb-6 flex items-center gap-4">
                  <div className={`flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br ${s.color} text-white shadow-lg shadow-${s.color.split('-')[1]}-500/30`}>
                    <Icon name={s.icon} className="h-7 w-7" />
                  </div>
                  <span className="text-4xl font-extrabold text-slate-100">{s.step}</span>
                </div>
                <h3 className="relative text-xl font-bold text-slate-900">{s.title}</h3>
                <p className="relative mt-3 text-slate-600">{s.description}</p>
                {index < 2 && (
                  <div className="absolute right-4 top-1/2 hidden translate-x-1/2 -translate-y-1/2 text-slate-300 md:block">
                    <ArrowRight className="h-6 w-6" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24" aria-labelledby="usecases-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <span className="inline-flex items-center gap-2 rounded-full border border-brand-200 bg-brand-50 px-4 py-1.5 text-sm font-semibold text-brand-700">
              <Users className="h-4 w-4" /> Team fit
            </span>
            <h2 id="usecases-heading" className="mt-6 text-3xl font-extrabold text-slate-900 sm:text-4xl">Built for every team</h2>
            <p className="mt-4 text-lg text-slate-600">One platform that adapts to how your business serves customers.</p>
          </div>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {[
              {
                icon: 'MessageSquare',
                title: 'Customer Support',
                description: 'Resolve tickets across every channel with AI and team collaboration.',
                color: 'from-blue-500 to-blue-600',
              },
              {
                icon: 'TrendingUp',
                title: 'Sales & Pre-Sales',
                description: 'Capture leads, answer questions, and convert visitors before they leave.',
                color: 'from-green-500 to-green-600',
              },
              {
                icon: 'Users',
                title: 'Customer Onboarding',
                description:
                  'Guide new customers with knowledge bases, live chat, and proactive check-ins.',
                color: 'from-purple-500 to-purple-600',
              },
              {
                icon: 'Settings',
                title: 'Operations',
                description: 'Route, tag, invoice, and report so nothing gets lost behind the scenes.',
                color: 'from-orange-500 to-orange-600',
              },
            ].map((u) => (
              <div
                key={u.title}
                className="group relative overflow-hidden rounded-3xl border border-slate-100 bg-white p-8 shadow-sm transition-all hover:-translate-y-1 hover:shadow-xl"
              >
                <div className="absolute right-0 top-0 h-20 w-20 translate-x-6 -translate-y-6 rounded-full bg-gradient-to-br from-slate-100 to-slate-50 opacity-50" />
                <div className="relative mb-5 flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br text-white shadow-lg shadow-${u.color.split('-')[1]}-500/30">
                  <Icon name={u.icon} className="h-6 w-6" />
                </div>
                <h3 className="relative text-xl font-bold text-slate-900">{u.title}</h3>
                <p className="relative mt-3 text-sm text-slate-600">{u.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden bg-gradient-to-br from-slate-900 to-slate-950 py-24 text-white" aria-labelledby="channels-heading">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-brand-900/30 via-slate-950 to-slate-950" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <span className="inline-flex items-center gap-2 rounded-full border border-brand-500/30 bg-brand-500/10 px-4 py-1.5 text-sm font-semibold text-brand-300">
              <MessageSquare className="h-4 w-4" /> True omnichannel
            </span>
            <h2 id="channels-heading" className="mt-6 text-3xl font-extrabold text-white sm:text-4xl">All your channels, one inbox</h2>
            <p className="mt-4 text-lg text-slate-300">
              Connect the channels your customers already use. Reply from a single, shared workspace.
            </p>
          </div>
          <div className="mt-14 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
            {channels.map((c) => (
              <div
                key={c.name}
                className="group flex flex-col items-center justify-center gap-3 rounded-2xl border border-slate-700/50 bg-slate-800/40 p-6 text-center transition-all hover:-translate-y-1 hover:border-brand-500/50 hover:bg-slate-800/60 hover:shadow-xl"
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-slate-700/50 text-white transition group-hover:scale-110 group-hover:bg-brand-600">
                  <BrandIcon name={c.brand} className="h-7 w-7" />
                </div>
                <span className="font-semibold text-slate-200">{c.name}</span>
              </div>
            ))}
          </div>
          <div className="mt-12 text-center">
            <p className="text-sm text-slate-400">
              <span className="font-semibold text-white">Connect in minutes</span> — No technical setup required
            </p>
          </div>
        </div>
      </section>

      <section className="py-20" aria-labelledby="pricing-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-12 text-center">
            <span className="inline-flex items-center gap-2 rounded-full border border-brand-200 bg-brand-50 px-4 py-1.5 text-sm font-semibold text-brand-700">
              <BarChart3 className="h-4 w-4" /> Transparent pricing
            </span>
            <h2 id="pricing-heading" className="mt-6 text-3xl font-extrabold text-slate-900 sm:text-4xl">Simple, fair pricing</h2>
            <p className="mt-4 text-lg text-slate-600">Start free. Scale as you grow. No hidden fees.</p>
          </div>
          <div className="mb-8 rounded-2xl border border-slate-200 bg-slate-50 p-5 text-sm text-slate-600">
            <p>
              <span className="font-semibold text-slate-900">All prices include VAT and applicable taxes.</span> Prices are quoted in USD and billed by Flutterwave. You can pay by card, Google Pay, Apple Pay, Airtel/MTN MoMo, or bank transfer.
            </p>
            <PaymentMethods className="mt-4" />
          </div>
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {pricingPlans.map((plan) => (
              <div
                key={plan.name}
                className={`relative flex flex-col rounded-3xl border p-8 shadow-sm transition hover:-translate-y-1 hover:shadow-xl ${
                  plan.featured
                    ? 'border-brand-300 bg-gradient-to-br from-brand-50 to-white ring-2 ring-brand-200 shadow-lg'
                    : 'border-slate-200 bg-white'
                }`}
              >
                {plan.featured && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-brand-600 to-indigo-600 px-4 py-1.5 text-xs font-semibold text-white shadow-md">
                    <Star className="h-3 w-3 fill-white" />
                    Most Popular
                  </span>
                )}
                <h3 className={`text-2xl font-bold ${plan.featured ? 'text-brand-900' : 'text-slate-900'}`}>{plan.name}</h3>
                <p className="mt-2 text-slate-600">{plan.description}</p>
                <div className="mt-6 flex items-baseline gap-1">
                  <span className={`text-4xl font-extrabold ${plan.featured ? 'text-brand-700' : 'text-slate-900'}`}>
                    {plan.priceUSD === 0 ? 'Free' : formatPrice(convertPrice(plan.priceUSD))}
                  </span>
                  <span className="text-sm text-slate-500">/{plan.period}</span>
                </div>
                <ul className="mt-6 flex-1 space-y-3">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-2 text-sm text-slate-700">
                      <Check className={`mt-0.5 h-4 w-4 flex-shrink-0 ${plan.featured ? 'text-brand-600' : 'text-green-600'}`} />
                      {feature}
                    </li>
                  ))}
                </ul>
                {plan.href.startsWith('http') ? (
                  <a
                    href={plan.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`mt-8 inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-base font-semibold transition ${
                      plan.featured
                        ? 'bg-gradient-to-r from-brand-600 to-indigo-600 text-white hover:from-brand-700 hover:to-indigo-700'
                        : 'border border-slate-300 bg-white text-slate-700 hover:border-brand-300 hover:text-brand-600'
                    }`}
                  >
                    {plan.cta} <ArrowRight className="h-4 w-4" />
                  </a>
                ) : (
                  <Link
                    to={plan.href}
                    className={`mt-8 inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-base font-semibold transition ${
                      plan.featured
                        ? 'bg-gradient-to-r from-brand-600 to-indigo-600 text-white hover:from-brand-700 hover:to-indigo-700'
                        : 'border border-slate-300 bg-white text-slate-700 hover:border-brand-300 hover:text-brand-600'
                    }`}
                  >
                    {plan.cta} <ArrowRight className="h-4 w-4" />
                  </Link>
                )}
              </div>
            ))}
          </div>
          <p className="mt-8 text-center text-sm text-slate-500">
            Looking for detailed feature comparisons and add-ons? <Link to="/pricing" className="font-semibold text-brand-600 hover:underline">See full pricing page</Link>.
          </p>
        </div>
      </section>

      <section className="py-24" aria-labelledby="product-faq-heading">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <span className="inline-flex items-center gap-2 rounded-full border border-brand-200 bg-brand-50 px-4 py-1.5 text-sm font-semibold text-brand-700">
              <Sparkles className="h-4 w-4" /> Quick answers
            </span>
            <h2 id="product-faq-heading" className="mt-6 text-3xl font-extrabold text-slate-900 sm:text-4xl">
              Common questions
            </h2>
            <p className="mt-4 text-lg text-slate-600">Everything you need to know about ZuriHub.</p>
          </div>
          <div className="mt-12 space-y-4">
            {productFaqs.map((faq) => (
              <details
                key={faq.q}
                className="group rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition-all hover:shadow-md"
              >
                <summary className="flex cursor-pointer list-none items-center justify-between text-lg font-bold text-slate-900">
                  {faq.q}
                  <ArrowRight className="h-5 w-5 text-slate-400 transition group-open:rotate-90" />
                </summary>
                <p className="mt-4 leading-relaxed text-slate-600">{faq.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden bg-gradient-to-br from-brand-600 to-brand-700 py-24 text-white" aria-label="Start your free trial">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom,_var(--tw-gradient-stops))] from-white/10 to-transparent" />
        <div className="relative mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
          <h2 className="text-3xl font-extrabold sm:text-4xl">Ready to modernize your customer experience?</h2>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-brand-100">
            Join teams that serve customers faster, cheaper, and happier on ZuriHub.
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <a
              href={site.workspaceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-8 py-4 text-base font-bold text-brand-700 shadow-xl transition hover:bg-slate-100"
            >
              Start Free Trial <ArrowUpRight className="h-4 w-4" />
            </a>
            <Link
              to="/book-a-demo"
              className="inline-flex items-center justify-center gap-2 rounded-full border border-white/30 bg-white/10 px-8 py-4 text-base font-semibold text-white backdrop-blur transition hover:bg-white/20"
            >
              Book a Demo <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <p className="mt-6 text-sm text-brand-200">No credit card required. Free forever plan for 1 agent.</p>
        </div>
      </section>
    </>
  );
}
